import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from './auth-store'
import { artistFetch } from './artist-api'
import { buildPerformanceQuery, filterRowsByShowIds } from './performance-query'

interface TourLegRow {
  shows: Array<{ showId: string }>
}

/**
 * Resolves the sidebar "active performance" into API query params and show-id
 * sets for client-side filtering when an endpoint only accepts showId.
 */
export function usePerformanceScope() {
  const active = useAuthStore((s) => s.activePerformance)

  const legsQ = useQuery<TourLegRow[]>({
    queryKey: ['perf-scope-legs', active?.kind === 'tour' ? active.id : null],
    queryFn: () => artistFetch<TourLegRow[]>(`/tours/${active!.id}/legs`),
    enabled: active?.kind === 'tour',
    staleTime: 60_000,
  })

  const showIds = useMemo((): string[] | null => {
    if (!active) return null
    if (active.kind === 'show') return [active.id]
    const ids = new Set<string>()
    for (const leg of legsQ.data ?? []) {
      for (const s of leg.shows ?? []) ids.add(s.showId)
    }
    return Array.from(ids)
  }, [active, legsQ.data])

  const query = useMemo(() => buildPerformanceQuery(active), [active])

  return {
    active,
    isScoped: !!active,
    scopeLabel: active?.name ?? null,
    kind: active?.kind ?? null,
    id: active?.id ?? null,
    tourId: active?.kind === 'tour' ? active.id : null,
    showId: active?.kind === 'show' ? active.id : null,
    showIds,
    isResolvingTour: active?.kind === 'tour' && legsQ.isLoading,
    queryKeySuffix: active ? ([active.kind, active.id] as const) : (['all'] as const),
    toQueryString: (extra?: Record<string, string>) => {
      const params = new URLSearchParams()
      if (query.tourId) params.set('tourId', query.tourId)
      if (query.showId) params.set('showId', query.showId)
      if (extra) {
        for (const [k, v] of Object.entries(extra)) {
          if (v) params.set(k, v)
        }
      }
      const qs = params.toString()
      return qs ? `?${qs}` : ''
    },
    filterByShowIds: <T,>(rows: T[], getShowId: (row: T) => string | null | undefined) =>
      filterRowsByShowIds(rows, getShowId, showIds),
  }
}
